import { z } from 'zod';
import { type Filter, filterSchema } from './validation';
import { getField, multiValues, OBJECT_NAMES, type ObjectDef } from './objects';

const uuid = z.string().uuid();
const objectEnum = z.enum(OBJECT_NAMES);
export const GRANTEE_TYPES = ['user', 'role', 'role_subordinates', 'group'] as const;
export type GranteeType = (typeof GRANTEE_TYPES)[number];
export type AccessLevel = 'read' | 'edit';

// --- Roles and groups ---------------------------------------------------------
export const roleSchema = z.object({ name: z.string().trim().min(1).max(80), parentRoleId: uuid.nullable().optional() }).strict();
export const groupSchema = z.object({ name: z.string().trim().min(1).max(80), userIds: z.array(uuid).max(1000).default([]) }).strict();
export const memberRoleSchema = z.object({ roleId: uuid.nullable() }).strict();

export interface Role { id: string; name: string; parentRoleId: string | null; memberCount: number }
export interface Group { id: string; name: string; userIds: string[] }

// --- Manual shares -----------------------------------------------------------------
export const shareSchema = z
  .object({ granteeType: z.enum(GRANTEE_TYPES), granteeId: uuid, access: z.enum(['read', 'edit']) })
  .strict();
export interface RecordShare {
  id: string;
  granteeType: GranteeType;
  granteeId: string;
  granteeName: string;
  access: AccessLevel;
  reason: 'manual' | 'rule';
  ruleName: string | null;
}

// --- Sharing rules -------------------------------------------------------------------
export const sharingRuleSchema = z
  .object({
    objectName: objectEnum,
    name: z.string().trim().min(1).max(80),
    kind: z.enum(['owner', 'criteria']),
    sourceType: z.enum(['role', 'role_subordinates', 'group']).nullable().optional(),
    sourceId: uuid.nullable().optional(),
    criteria: z.array(filterSchema).min(1).max(10).nullable().optional(),
    targetType: z.enum(['role', 'role_subordinates', 'group']),
    targetId: uuid,
    access: z.enum(['read', 'edit']),
    isActive: z.boolean().default(true),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.kind === 'owner' && (!r.sourceType || !r.sourceId)) ctx.addIssue({ code: 'custom', path: ['sourceId'], message: 'Choose whose records to share' });
    if (r.kind === 'criteria' && !r.criteria?.length) ctx.addIssue({ code: 'custom', path: ['criteria'], message: 'Add at least one condition' });
  });
export type SharingRuleInput = z.infer<typeof sharingRuleSchema>;
export interface SharingRule extends SharingRuleInput { id: string }

// --- Teams --------------------------------------------------------------------------------
export const accountTeamMemberSchema = z
  .object({
    userId: uuid,
    teamRole: z.string().trim().min(1).max(60).default('member'),
    accountAccess: z.enum(['read', 'edit']).default('read'),
    contactAccess: z.enum(['none', 'read', 'edit']).default('read'),
    opportunityAccess: z.enum(['none', 'read', 'edit']).default('read'),
  })
  .strict();
export const opportunityTeamMemberSchema = z
  .object({ userId: uuid, teamRole: z.string().trim().min(1).max(60).default('member'), access: z.enum(['read', 'edit']).default('read') })
  .strict();
export interface TeamMember {
  id: string;
  userId: string;
  userName: string;
  teamRole: string;
  access?: AccessLevel;
  accountAccess?: AccessLevel;
  contactAccess?: 'none' | AccessLevel;
  opportunityAccess?: 'none' | AccessLevel;
}

// --- Validation and assignment rules ---------------------------------------------
/**
 * A validation rule blocks a save when its conditions all match, or (formula rules) when its
 * formula is true. Formulas can also use ISNEW(), ISCHANGED(field) and PRIORVALUE(field).
 */
export const validationRuleSchema = z
  .object({
    objectName: objectEnum,
    name: z.string().trim().min(1).max(80),
    conditions: z.array(filterSchema).max(10).default([]),
    formula: z.string().trim().min(1).max(3000).nullable().optional(),
    errorMessage: z.string().trim().min(1).max(255),
    errorField: z.string().max(64).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .strict()
  .refine((r) => (r.formula ? r.conditions.length === 0 : r.conditions.length > 0), {
    path: ['conditions'], message: 'Use either conditions or a formula',
  });
export type ValidationRuleInput = z.infer<typeof validationRuleSchema>;
export interface ValidationRule extends ValidationRuleInput {
  id: string;
  /** Set when a formula rule no longer compiles (for example a field it used changed); such a rule blocks nothing. */
  formulaError?: string | null;
}

export const assignmentRuleSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    isActive: z.boolean().default(false),
    entries: z
      .array(
        z.object({
          conditions: z.array(filterSchema).max(10).default([]),
          assignType: z.enum(['user', 'round_robin', 'queue']),
          userIds: z.array(uuid).max(100).default([]),
          /** For assignType 'queue'. */
          queueId: uuid.nullable().optional(),
        }).strict()
          .refine((e) => (e.assignType === 'queue' ? !!e.queueId : e.userIds.length >= 1), { message: 'Choose who receives the leads', path: ['userIds'] })
          .refine((e) => e.assignType !== 'user' || e.userIds.length === 1, { message: 'Assign to exactly one user, or use round robin', path: ['userIds'] }),
      )
      .min(1)
      .max(25),
  })
  .strict();
export type AssignmentRuleInput = z.infer<typeof assignmentRuleSchema>;
export interface AssignmentRule extends AssignmentRuleInput { id: string; objectName: 'lead' }

// --- Notifications ------------------------------------------------------------------
export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  objectName: string | null;
  recordId: string | null;
  actorName: string | null;
  createdAt: string;
  readAt: string | null;
}

// --- Duplicates ------------------------------------------------------------------------
export interface DuplicateMatch { objectName: string; id: string; name: string; reason: string }

/**
 * Evaluates filters against an in-memory record (all must match). Mirrors the SQL semantics in
 * the API's filterFragment: text comparisons are case-insensitive, null never equals a value.
 */
export function evaluateFilters(obj: ObjectDef, rec: Record<string, unknown>, filters: readonly Filter[]): boolean {
  return filters.every((f) => evaluateFilter(obj, rec, f));
}

function norm(v: unknown): unknown {
  if (v instanceof Date) return v.toISOString();
  return typeof v === 'string' ? v.toLowerCase() : v;
}

export function evaluateFilter(obj: ObjectDef, rec: Record<string, unknown>, f: Filter): boolean {
  const field = getField(obj, f.field);
  if (!field) return false;
  const raw = rec[f.field];
  const isEmpty = raw === null || raw === undefined || raw === '';
  // Multi-select: "equals" means "includes", "in" means "includes any".
  if (field.type === 'multipicklist' && (f.op === 'eq' || f.op === 'ne' || f.op === 'in')) {
    const have = multiValues(raw).map((v) => v.toLowerCase());
    const want = (Array.isArray(f.value) ? f.value : [f.value]).map((v) => String(v ?? '').toLowerCase());
    const hit = want.some((v) => have.includes(v));
    return f.op === 'ne' ? !hit : hit;
  }
  switch (f.op) {
    case 'isNull':
      return isEmpty;
    case 'notNull':
      return !isEmpty;
    case 'eq':
      return !isEmpty && norm(raw) === norm(f.value);
    case 'ne':
      return isEmpty || norm(raw) !== norm(f.value);
    case 'in':
      return !isEmpty && Array.isArray(f.value) && f.value.map(norm).includes(norm(raw));
    case 'contains':
      return !isEmpty && String(raw).toLowerCase().includes(String(f.value ?? '').toLowerCase());
    case 'startsWith':
      return !isEmpty && String(raw).toLowerCase().startsWith(String(f.value ?? '').toLowerCase());
    default: {
      if (isEmpty || f.value === null || f.value === undefined) return false;
      const numeric = ['number', 'currency', 'percent'].includes(field.type);
      const a = numeric ? Number(raw) : String(norm(raw));
      const b = numeric ? Number(f.value) : String(norm(f.value));
      if (f.op === 'lt') return a < b;
      if (f.op === 'lte') return a <= b;
      if (f.op === 'gt') return a > b;
      return a >= b;
    }
  }
}
